import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { formatEur } from "@/lib/plans";
import type { AppliedPromo } from "../promo";

interface Props {
  applied: AppliedPromo | null;
  /** Already translated. */
  error: string | null;
  checking: boolean;
  disabled?: boolean;
  onApply: (code: string) => void;
  onRemove: () => void;
}

/** "Have a promo code?" in the order summary: type, apply, or remove. */
export default function PromoCodeField({ applied, error, checking, disabled, onApply, onRemove }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");

  const apply = () => {
    const trimmed = code.trim();
    if (trimmed && !checking) onApply(trimmed);
  };

  // The field sits inside the checkout form: Enter applies the code instead
  // of submitting the order.
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    }
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm">
        <span className="flex min-w-0 items-center gap-2 text-primary-800">
          <i className="ri-coupon-3-line text-base"></i>
          <span className="truncate">
            {t("checkout.promo.applied", { code: applied.code })} · −{formatEur(applied.discountCents)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => {
            setCode("");
            onRemove();
          }}
          disabled={disabled}
          className="shrink-0 text-xs font-semibold text-primary-700 hover:underline disabled:opacity-60 cursor-pointer"
        >
          {t("checkout.promo.remove")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="promoCode" className="block text-xs font-medium text-foreground-600">
        {t("checkout.promo.label")}
      </label>
      <div className="mt-1.5 flex gap-2">
        <input
          id="promoCode"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={40}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={onKeyDown}
          placeholder={t("checkout.promo.placeholder")}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "promoCodeError" : undefined}
          disabled={disabled}
          className="min-w-0 flex-1 rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-sm uppercase text-foreground-950 placeholder:normal-case placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        />
        <button
          type="button"
          onClick={apply}
          disabled={disabled || checking || !code.trim()}
          className="shrink-0 rounded-lg border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 hover:text-primary-700 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {checking ? t("checkout.promo.applying") : t("checkout.promo.apply")}
        </button>
      </div>
      {error && (
        <p id="promoCodeError" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
